/* eslint class-methods-use-this: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { withTranslation, Trans, useTranslation } from 'react-i18next';
import { Pencil, AlertCircle } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@cargomatic/ui';
import SessionStore from 'stores/SessionStore';
import { isDrayageShipment } from 'helpers/shipment-types';
import SelectReasonCodes from 'components/modules/marketplace-config/SelectReasonCodes';
import { titleize } from 'underscore.string';
import SoftAlertHoldNotification from 'components/modules/shipments/stops/SoftAlertHoldNotification';
import CargomaticDatePicker from 'components/v2/CargomaticDatePicker';
import TimePicker from 'components/v2/TimePicker';
import { processDateTimeChange, parseTimeInput } from 'utils/formatters/Date';
import { withLocaleContext } from 'providers/locale-settings-context';
import { getLocationTimezone } from '../../../../helpers/locations';

const ERRORS = {
  END_BEFORE_START: 'START_DATE_BEFORE_END_DATE',
};

const DRAYAGE_DEFAULT_WINDOW_HOURS = 1;
const SHORTHAUL_DEFAULT_WINDOW_HOURS = 4;

const formatTime = (momentValue, is24HourFormat) => {
  if (!momentValue) return '';
  return momentValue.format(is24HourFormat ? 'HH:mm' : 'hh:mm A');
};

const getInitialState = (props) => {
  const { stop, shipment } = props;

  const { shipment_type, isStaging } = shipment;

  const drayageShipment = isDrayageShipment(shipment);

  const { empty, window_start, window_end } = stop;

  let timezone;
  if (isStaging) {
    timezone = props.selected_timezone;
  } else {
    timezone = getLocationTimezone(stop.location);
  }
  const today = moment.tz(moment(), timezone);
  const date = today.toDate();
  const startHour = date.getHours() + Math.round(date.getMinutes() / 60) + 1;

  date.setHours(startHour, 0, 0);

  const defaultEnd = today.clone();
  const endDate = defaultEnd.toDate();
  const endHour = startHour + 1;
  endDate.setHours(endHour, 0, 0);

  const momentStartDate = window_start
    ? moment.tz(window_start, timezone)
    : moment.tz(date, timezone);
  const momentEndDate = window_end
    ? moment.tz(window_end, timezone)
    : moment.tz(endDate, timezone);

  let start;
  let end;

  if (momentStartDate && momentStartDate.isValid()) {
    start = momentStartDate;
  } else {
    start = null;
  }

  if (momentEndDate && momentEndDate.isValid()) {
    end = momentEndDate;
  } else {
    end = null;
  }

  let appointment_unconfirmed = stop.appointment_unconfirmed;
  if (!start && !end) {
    appointment_unconfirmed = true;
  }

  if (empty || !drayageShipment) {
    appointment_unconfirmed = false;
  }

  return {
    drayageShipment,
    timezone,
    start,
    end,
    // Raw text mirrored into the TimePicker inputs. The TimePicker is a fully
    // controlled react-input-mask, so its value must echo exactly what the
    // user types — deriving it from `start`/`end` makes incomplete keystrokes
    // ("04:4_ PM") fall back to a date-only moment and snap the field to
    // "12:00 AM", blocking edits.
    startTimeText: formatTime(start, props.localeData?.is24HourFormat),
    endTimeText: formatTime(end, props.localeData?.is24HourFormat),
    appointment_unconfirmed,
    hideConfirm: stop.empty || shipment_type !== 'drayage',
    errors: [],
    isValid: true,
    name: '',
    value: '',
    reason: null,
    comment: '',
    commentRequired: false,
    requireCodes: false,
  };
};

const ErrorList = (props) => {
  const { t } = useTranslation();
  return (
    <Alert variant="destructive">
      <AlertCircle className="size-4" />
      <AlertTitle>{t('FIX_BELOW_ERRORS')}</AlertTitle>
      <AlertDescription>
        <ul className="ml-4 list-disc">
          {props.errors.map((error, i) => (
            <li key={i}>{t(error)}</li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
};

ErrorList.propTypes = {
  errors: PropTypes.array,
};

ErrorList.defaultProps = {
  errors: [],
};

class AddAppointmentModal extends Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    onSave: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = getInitialState(props);
  }

  setCodesRequired = (requireCodes) => {
    this.setState({ requireCodes });
  };

  handleReasonSelect = (reason) => {
    const { code, description, reasonType } = reason;

    this.setState(
      {
        name: description,
        value: code,
        reason_type: reasonType,
      },
      this.validate,
    );
  };

  handleCommentInput = (comment) => {
    this.setState({ comment }, this.validate);
  };

  handleCommentRequired = (value) => {
    this.setState({ commentRequired: value }, this.validate);
  };

  handleSave = () => {
    const {
      start,
      end,
      appointment_unconfirmed,
      timezone,
      name,
      value,
      reason_type,
      comment,
    } = this.state;
    let window_start;
    let window_end;
    if (timezone) {
      window_start = start
        ? moment.utc(moment(start.tz(timezone, true)).format())
        : null;

      window_end = end
        ? moment.utc(moment(end).tz(timezone, true).format())
        : null;
    } else {
      window_start = start ? moment.utc(moment(start).format()) : null;
      window_end = end ? moment.utc(moment(end).format()) : null;
    }

    const updates = {
      window_start,
      window_end,
      appointment: true,
      appointment_unconfirmed,
      name,
      value,
      reason_type,
    };

    if (comment && comment.length) {
      updates.comment = comment;
    }

    if (!appointment_unconfirmed) {
      updates.appointment_unconfirmed = false;
    }

    if (!window_start && !window_end) {
      updates.appointment = false;
      updates.appointment_unconfirmed = true;
    }

    this.props.onSave(updates);
  };

  handleToggleConfirmed = (confirmed) =>
    this.setState({ appointment_unconfirmed: !confirmed }, () =>
      this.validate(),
    );

  handleStartDateChange = (start, { startText } = {}) => {
    this.setState(
      (prevState) => {
        const { end: currentEnd } = prevState;

        let end = currentEnd;

        const momentStart = moment(start);
        if (start) {
          const hours = prevState.drayageShipment
            ? DRAYAGE_DEFAULT_WINDOW_HOURS
            : SHORTHAUL_DEFAULT_WINDOW_HOURS;
          if (!end) {
            end = momentStart.add(hours, 'hours');
          }
        }

        let { appointment_unconfirmed } = prevState;
        if (!prevState.hideConfirm && (!end || !start)) {
          appointment_unconfirmed = true;
        }

        const is24 = this.props.localeData?.is24HourFormat;
        return {
          start,
          end,
          appointment_unconfirmed,
          // Echo raw text for a directly-typed time; otherwise normalize from
          // the resulting moment. Only sync the counterpart when it auto-fills.
          startTimeText:
            startText !== undefined ? startText : formatTime(start, is24),
          endTimeText: currentEnd
            ? prevState.endTimeText
            : formatTime(end, is24),
        };
      },
      () => this.validate(),
    );
  };

  handleEndDateChange = (end, { endText } = {}) => {
    this.setState(
      (prevState) => {
        const { start: currentStart } = prevState;

        let start = currentStart;

        const momentEnd = moment(end);

        if (end) {
          const hours = prevState.drayageShipment
            ? DRAYAGE_DEFAULT_WINDOW_HOURS
            : SHORTHAUL_DEFAULT_WINDOW_HOURS;
          if (!start) {
            start = momentEnd.subtract(hours, 'hours');
          }
        }

        let { appointment_unconfirmed } = prevState;
        if (!prevState.hideConfirm && (!end || !start)) {
          appointment_unconfirmed = true;
        }

        const is24 = this.props.localeData?.is24HourFormat;
        return {
          appointment_unconfirmed,
          start,
          end,
          endTimeText: endText !== undefined ? endText : formatTime(end, is24),
          startTimeText: currentStart
            ? prevState.startTimeText
            : formatTime(start, is24),
        };
      },
      () => this.validate(),
    );
  };

  // Build a moment-in-tz from a date input + time input. Either may be missing.
  combineToTzMoment = (dateInput, timeInput) => {
    const { timezone } = this.state;
    const { localeData } = this.props;
    if (!dateInput && !timeInput) return null;

    const dateStr = dateInput
      ? moment(dateInput).format('MM/DD/YYYY')
      : moment.tz(timezone).format('MM/DD/YYYY');
    const timeStr =
      timeInput || (localeData?.is24HourFormat ? '00:00' : '12:00 AM');

    const result = processDateTimeChange({
      date: dateStr,
      time: timeStr,
      timezone,
      is24HourTime: localeData?.is24HourFormat,
    });
    return result.date || null;
  };

  handleStartDateOnlyChange = (newDate) => {
    if (!newDate) {
      this.handleStartDateChange(null);
      return;
    }
    const currentTime =
      this.state.startTimeText ||
      formatTime(this.state.start, this.props.localeData?.is24HourFormat) ||
      null;
    this.handleStartDateChange(this.combineToTzMoment(newDate, currentTime));
  };

  // Generic time-only handler shared by the start and end fields. Always
  // echoes the raw text so the masked TimePicker stays editable, but only
  // recomputes/stores the moment once the typed time is complete and valid —
  // a partial entry ("04:4_ PM") otherwise persists a wrong, date-only time
  // while the field still shows incomplete text.
  handleTimeOnlyChange = (field, newTime) => {
    if (!parseTimeInput(newTime)) {
      this.setState({ [`${field}TimeText`]: newTime });
      return;
    }
    const combined = this.combineToTzMoment(
      this.state[field] || new Date(),
      newTime,
    );
    if (field === 'start') {
      this.handleStartDateChange(combined, { startText: newTime });
    } else {
      this.handleEndDateChange(combined, { endText: newTime });
    }
  };

  handleStartTimeOnlyChange = (e) =>
    this.handleTimeOnlyChange('start', e.target.value);

  handleEndDateOnlyChange = (newDate) => {
    if (!newDate) {
      this.handleEndDateChange(null);
      return;
    }
    const currentTime =
      this.state.endTimeText ||
      formatTime(this.state.end, this.props.localeData?.is24HourFormat) ||
      null;
    this.handleEndDateChange(this.combineToTzMoment(newDate, currentTime));
  };

  handleEndTimeOnlyChange = (e) =>
    this.handleTimeOnlyChange('end', e.target.value);

  validate = () => {
    const { requireCodes, name, comment, commentRequired } = this.state;

    const errors = [];
    let isValid = true;

    if (
      this.state.start &&
      this.state.end &&
      moment(this.state.end).isBefore(this.state.start)
    ) {
      errors.push(ERRORS.END_BEFORE_START);
      isValid = false;
    }

    if (requireCodes && !name) {
      errors.push('REASON_CODE_REQUIRED_TO_UPDATE_APPOINMENT');

      if (commentRequired && (!comment || !comment.length)) {
        errors.push('REASON_CODE_COMMENT_REQUIRED');
      }
    }

    this.setState({ errors, isValid });
  };

  renderSoftAlertForHolds() {
    const { shipment } = this.props;
    if (!shipment || !shipment.holds) return null;
    return <SoftAlertHoldNotification shipment={shipment} />;
  }

  renderWindowFields() {
    const { t, localeData } = this.props;
    const {
      start,
      end,
      timezone,
      hideConfirm,
      appointment_unconfirmed,
      startTimeText,
      endTimeText,
    } = this.state;
    const dateFormat = localeData?.dateFormat || 'MM/DD/YYYY';
    const tzAbbr = timezone ? moment.tz(timezone).zoneAbbr() : '';

    // Build a local Date whose calendar day matches the moment-in-tz day so
    // the calendar shows the correct day regardless of the browser's tz.
    const toCalendarDate = (m) =>
      m ? new Date(m.year(), m.month(), m.date()) : null;

    const startDateValue = toCalendarDate(start);
    const endDateValue = toCalendarDate(end);
    const startTimeValue = startTimeText;
    const endTimeValue = endTimeText;

    const confirmedDisabled = !start || !end;
    const confirmedChecked = !!start && !!end && !appointment_unconfirmed;

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>{t('WINDOW_START_WITH_TIMEZONE', { timezone: tzAbbr })}</Label>
          <div className="flex flex-wrap items-center gap-2">
            <CargomaticDatePicker
              dataTestId="DatePicker-window-start"
              placeholderText={dateFormat}
              dateFormat={dateFormat}
              selected={startDateValue}
              onChange={this.handleStartDateOnlyChange}
              isClearable
            />
            <TimePicker
              dataTestId="TimePicker-window-start"
              value={startTimeValue}
              onChange={this.handleStartTimeOnlyChange}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('WINDOW_END_WITH_TIMEZONE', { timezone: tzAbbr })}</Label>
          <div className="flex flex-wrap items-center gap-2">
            <CargomaticDatePicker
              dataTestId="DatePicker-window-end"
              placeholderText={dateFormat}
              dateFormat={dateFormat}
              selected={endDateValue}
              onChange={this.handleEndDateOnlyChange}
              isClearable
            />
            <TimePicker
              dataTestId="TimePicker-window-end"
              value={endTimeValue}
              onChange={this.handleEndTimeOnlyChange}
            />
          </div>
        </div>

        {!hideConfirm && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="appointment-confirmed"
              data-testid="checkbox-appointment-confirmed"
              checked={confirmedChecked}
              disabled={confirmedDisabled}
              onCheckedChange={(checked) =>
                this.handleToggleConfirmed(!!checked)
              }
            />
            <Label htmlFor="appointment-confirmed">{t('CONFIRMED')}?</Label>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { drayageShipment } = this.state;
    const { t, dataTestId } = this.props;

    const hours = drayageShipment
      ? DRAYAGE_DEFAULT_WINDOW_HOURS
      : SHORTHAUL_DEFAULT_WINDOW_HOURS;
    const unit = t('HOUR', { count: hours });
    const hoursText = `${hours} ${unit}`;

    const handleOpenChange = (open) => {
      if (!open) {
        this.props.onHide();
      }
    };

    return (
      <Dialog open={this.props.show} onOpenChange={handleOpenChange}>
        <DialogContent
          data-testid={dataTestId}
          className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0"
        >
          <DialogHeader className="border-b px-6 py-4 flex-none">
            <DialogTitle>{t('ADD_EDIT_APPOINTMENT')}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {this.renderSoftAlertForHolds()}

            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-md text-sm">
              <span className="px-2 py-0.5 text-xs font-bold uppercase rounded-full bg-green-600 text-white">
                {t('PRO_TIP')}
              </span>
              <Pencil className="size-3 text-green-700" />
              <span className="text-green-800">
                <Trans
                  i18nKey="APPOINTMENT_WINDOW_MESSAGE"
                  values={{ hoursText }}
                  components={[<strong key="bold" />]}
                />
              </span>
            </div>

            {this.state.errors.length > 0 && (
              <ErrorList errors={this.state.errors} />
            )}

            {this.renderWindowFields()}

            {SessionStore.isAdmin() && (
              <SelectReasonCodes
                shipment={this.props.shipment}
                category="stop_window_change"
                updateComment={this.handleCommentInput}
                selectReasonCode={this.handleReasonSelect}
                updateCommentRequired={this.handleCommentRequired}
                useDescriptionAsValue
                setCodesRequired={this.setCodesRequired}
              />
            )}
          </div>

          <DialogFooter className="border-t px-6 py-4 flex-none">
            <Button variant="outline" onClick={this.props.onHide}>
              {t('CANCEL')}
            </Button>
            <Button
              onClick={this.handleSave}
              disabled={!this.state.isValid}
              data-testid="button-964027fe"
            >
              {titleize(t('SAVE'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}

export default withTranslation()(withLocaleContext(AddAppointmentModal));
